<template>
  <div
    v-if="showEmptyCell"
    data-testid="c-collaborate-integration-cell-renderer__empty-cell"
  />
  <div v-else class="w-full" data-testid="collaborate-integration-cell__link">
    <BittsTooltip
      v-if="submittedOn"
      :mount-to-body="true"
      placement="bottomLeft"
    >
      <template #default>
        <div
          class="flex items-center truncate"
          data-testid="c-collaborate-integration-cell-renderer__status"
        >
          <BittsSvg
            svg="partnerStackAltLogo"
            class="w-16 h-16 mr-8"
            alt="PartnerStack"
          />
          <p class="truncate">
            {{ formattedCellValue }}
          </p>
        </div>
      </template>
      <template #title> Sent to {{ company }} on {{ formattedDate }} </template>
    </BittsTooltip>
    <BittsButton
      v-else-if="isReport"
      text="Refer Lead"
      size="x-small"
      type="neutral"
      variant="outline"
      @click.stop="onReferLeadClick"
    >
      <template #icon>
        <BittsSvg svg="partnerStackAllBlueLogo" class="w-12 h-12 mr-4" alt="" />
      </template>
    </BittsButton>
  </div>
</template>
<script setup lang="ts">
import { BittsButton, BittsSvg, BittsTooltip } from '@crossbeam/bitts';

import { ICellRendererParams } from '@ag-grid-community/core';
import { DateTime } from 'luxon';
import { computed } from 'vue';

import { useForwardCellFocus } from '@/composables/useForwardCellFocus';
import { ReportDataItem } from '@/types/reports';
import {
  SharedListDataRow,
  SharedListIntegrationCellValue,
} from '@/types/shared_lists';

const props = defineProps<{
  params: ICellRendererParams<
    SharedListDataRow | ReportDataItem,
    SharedListIntegrationCellValue
  >;
}>();

useForwardCellFocus(props.params.eGridCell);

const showEmptyCell = computed(() => {
  return (
    props.params.data &&
    'is_greenfield' in props.params.data &&
    props.params.data.is_greenfield &&
    !props.params.data.record_id &&
    !isReport.value
  );
});

const formattedDate = computed(() =>
  DateTime.fromISO(submittedOn.value || '').toFormat('MMM d, yyyy'),
);

const submittedOn = computed(
  () =>
    props.params.value?.submitted_on ||
    (props.params.data &&
      '_xb_partnerstack' in props.params.data &&
      props.params.data._xb_partnerstack?.submitted_on),
);
const company = computed(
  () =>
    props.params.value?.company ||
    (props.params.data &&
      '_xb_partnerstack' in props.params.data &&
      props.params.data._xb_partnerstack?.company),
);
const isReport = computed(
  () =>
    props.params.data &&
    'master_uuid' in props.params.data &&
    props.params.data.master_uuid,
);

const formattedCellValue = computed(() => `Sent ${formattedDate.value}`);

function onReferLeadClick() {
  const context = props.params.context;
  const rowData = props.params.data;
  const node = props.params.node;
  const rowIndex = node?.rowIndex ?? 0;
  if (context?.onReferLeadClicked && rowData) {
    context.onReferLeadClicked(rowData, rowIndex);
  }
}
</script>
